'use client'

import { useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { isRecaptchaBypassed } from '@/lib/recaptcha'

const BUDGET_OPTIONS = [
  'Under $500 / month',
  '$500–$2,000 / month',
  '$2,000–$5,000 / month',
  '$5,000+ / month',
  'One-time campaign',
  'Not sure yet',
]

const inputClass =
  'w-full px-4 py-2.5 bg-white border border-slate-300 text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-[#1E4289] focus:border-transparent rounded'

const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''
const skipRecaptcha = isRecaptchaBypassed()
const captchaReady = skipRecaptcha || Boolean(siteKey)

export function AdvertiseForm() {
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    website: '',
    budget: '',
    message: '',
  })

  const update = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    if (!skipRecaptcha && !siteKey) {
      setError('reCAPTCHA is not configured. Please try again later.')
      setIsSubmitting(false)
      return
    }

    if (!skipRecaptcha && !captchaToken) {
      setError('Please complete the reCAPTCHA challenge.')
      setIsSubmitting(false)
      return
    }

    try {
      const res = await fetch('/api/advertise', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, recaptchaToken: captchaToken }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit. Please try again.')
      }
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit. Please try again.')
      recaptchaRef.current?.reset()
      setCaptchaToken(null)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#CCF2DC] flex items-center justify-center">
          <svg className="w-7 h-7 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-[#1E4289] mb-2">Thanks — we got it</h2>
        <p className="text-slate-600 max-w-md mx-auto">
          We&apos;ll review your inquiry and follow up at <strong>{formData.email}</strong> soon.
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 sm:p-8 space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1.5">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            required
            maxLength={120}
            value={formData.name}
            onChange={update('name')}
            className={inputClass}
            placeholder="Your name"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            required
            maxLength={200}
            value={formData.email}
            onChange={update('email')}
            className={inputClass}
            placeholder="you@company.com"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-1.5">
            Company / brand
          </label>
          <input
            id="company"
            type="text"
            maxLength={200}
            value={formData.company}
            onChange={update('company')}
            className={inputClass}
            placeholder="Optional"
          />
        </div>
        <div>
          <label htmlFor="website" className="block text-sm font-medium text-slate-700 mb-1.5">
            Website
          </label>
          <input
            id="website"
            type="url"
            maxLength={300}
            value={formData.website}
            onChange={update('website')}
            className={inputClass}
            placeholder="https://"
          />
        </div>
      </div>

      <div>
        <label htmlFor="budget" className="block text-sm font-medium text-slate-700 mb-1.5">
          Approximate budget
        </label>
        <select
          id="budget"
          value={formData.budget}
          onChange={update('budget')}
          className={inputClass}
        >
          <option value="">Select an option (optional)</option>
          {BUDGET_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1.5">
          Tell us about your goals <span className="text-red-500">*</span>
        </label>
        <textarea
          id="message"
          required
          rows={5}
          minLength={10}
          maxLength={5000}
          value={formData.message}
          onChange={update('message')}
          className={inputClass}
          placeholder="What are you promoting, and what kind of placement are you interested in?"
        />
      </div>

      {!skipRecaptcha && (
        <div>
          {siteKey ? (
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={siteKey}
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
              onErrored={() => setCaptchaToken(null)}
            />
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              reCAPTCHA is not configured (missing NEXT_PUBLIC_RECAPTCHA_SITE_KEY).
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting || !captchaReady}
        className="w-full sm:w-auto px-8 py-3 bg-[#1E4289] text-white font-semibold rounded hover:bg-[#FEDB1A] hover:text-slate-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Sending…' : 'Submit inquiry'}
      </button>
    </form>
  )
}
